import { resizeHandler } from '@/components/EmbeddedResize/EmbeddedResize'
import { rbp } from '@/constants/measured-scope'
import { eventsTracker, UIElementNames } from '@/lib/eventsTracker'
import { useMediaQuery } from '@react-hookz/web'
import { useEffect, useState } from 'react'

interface AssistantPromptCardLayout {
  numberOfItemsToDisplay: number
  isMaxNumberOfItemsToDisplay: boolean
  onViewMoreItems(event?: React.MouseEvent<HTMLElement>): void
}

type Breakpoint = 'mobile' | 'smallTablet' | 'tablet' | 'desktop' | 'largeDesktop'

type CardLayoutConfig = {
  [k in Breakpoint]: {
    initialAllowedNumberOfItemsToDisplay: number
    batchSize: number
  }
}

export const cardLayoutConfig: CardLayoutConfig = {
  mobile: {
    initialAllowedNumberOfItemsToDisplay: 4,
    batchSize: 4,
  },
  smallTablet: {
    initialAllowedNumberOfItemsToDisplay: 6,
    batchSize: 3,
  },
  tablet: {
    initialAllowedNumberOfItemsToDisplay: 8,
    batchSize: 4,
  },
  desktop: {
    initialAllowedNumberOfItemsToDisplay: 10,
    batchSize: 5,
  },
  largeDesktop: {
    initialAllowedNumberOfItemsToDisplay: 12,
    batchSize: 6,
  },
}

export function useAssistantPromptCardLayout(itemsLength: number): AssistantPromptCardLayout {
  const isSmallTabletAndAbove = useMediaQuery(`only screen and (min-width: ${rbp.smallTablet})`)
  const isTabletAndAbove = useMediaQuery(`only screen and (min-width: ${rbp.tablet})`)
  const isDesktopAndAbove = useMediaQuery(`only screen and (min-width: ${rbp.desktop})`)
  const isLargeDesktopAndAbove = useMediaQuery(`only screen and (min-width: ${rbp.largeDesktop})`)

  // Determine config for current breakpoint
  function getConfig(): { initialAllowedNumberOfItemsToDisplay: number; batchSize: number } {
    if (isLargeDesktopAndAbove) return cardLayoutConfig.largeDesktop
    if (isDesktopAndAbove) return cardLayoutConfig.desktop
    if (isTabletAndAbove) return cardLayoutConfig.tablet
    if (isSmallTabletAndAbove) return cardLayoutConfig.smallTablet
    return cardLayoutConfig.mobile
  }

  const [numberOfItemsToDisplay, setNumberOfItemsToDisplay] = useState(getConfig().initialAllowedNumberOfItemsToDisplay)

  // Reset numberOfItemsToDisplay when breakpoint changes
  useEffect(() => {
    setNumberOfItemsToDisplay(getConfig().initialAllowedNumberOfItemsToDisplay)
    resizeHandler()
  }, [isTabletAndAbove, isDesktopAndAbove, isSmallTabletAndAbove, isLargeDesktopAndAbove])

  // Calculate if we've reached the max (all items shown)
  const isMaxNumberOfItemsToDisplay = numberOfItemsToDisplay >= itemsLength

  function onViewMoreItems(event?: React.MouseEvent<HTMLElement>): void {
    eventsTracker.handleOnClickWithDistinctionAndTrackEvent(
      UIElementNames.LINK_VIEW_MORE,
      event?.currentTarget.getAttribute('data-distinction'),
      () => {
        const { batchSize } = getConfig()
        setNumberOfItemsToDisplay((prev) => Math.min(prev + batchSize, itemsLength))
        resizeHandler()
      },
    )
  }

  return { numberOfItemsToDisplay, isMaxNumberOfItemsToDisplay, onViewMoreItems }
}
